/** Word-level diff for LLM output pairs — pure, no React. Whitespace is kept
 *  as tokens so reassembly is byte-faithful; a guard bails out on
 *  pathologically large middles rather than freezing the tab. */

export const DiffType = {
  Equal: "equal",
  Added: "added",
  Removed: "removed",
} as const;
export type DiffTypeValue = (typeof DiffType)[keyof typeof DiffType];

export interface DiffSegment {
  type: DiffTypeValue;
  text: string;
}

const tokenize = (s: string): string[] => s.split(/(\s+)/).filter(Boolean);

/** Above this (middle-tokens product), the LCS table is too big to build
 *  in the render path — callers fall back to showing the texts unpaired. */
const MAX_LCS_CELLS = 1_000_000;

/** Prefix/suffix trim + LCS table over the middle — the DP core shared by
 *  the diff and the similarity score. Null when over MAX_LCS_CELLS. */
const trimmedLcs = (
  a: string[],
  b: string[],
): { start: number; aEnd: number; bEnd: number; width: number; lcs: Uint32Array } | null => {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let aEnd = a.length;
  let bEnd = b.length;
  while (aEnd > start && bEnd > start && a[aEnd - 1] === b[bEnd - 1]) {
    aEnd--;
    bEnd--;
  }
  const n = aEnd - start;
  const m = bEnd - start;
  if (n * m > MAX_LCS_CELLS) return null;
  const width = m + 1;
  const lcs = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * width + j] =
        a[start + i] === b[start + j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
    }
  }
  return { start, aEnd, bEnd, width, lcs };
};

export function wordDiff(before: string, after: string): DiffSegment[] | null {
  const a = tokenize(before);
  const b = tokenize(after);
  const core = trimmedLcs(a, b);
  if (core === null) return null;
  const { start, aEnd, bEnd, width, lcs } = core;
  const n = aEnd - start;
  const m = bEnd - start;

  const segments: DiffSegment[] = [];
  const push = (type: DiffTypeValue, text: string) => {
    if (!text) return;
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.text += text;
    else segments.push({ type, text });
  };

  push(DiffType.Equal, a.slice(0, start).join(""));
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[start + i] === b[start + j]) {
      push(DiffType.Equal, a[start + i]);
      i++;
      j++;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
      push(DiffType.Removed, a[start + i]);
      i++;
    } else {
      push(DiffType.Added, b[start + j]);
      j++;
    }
  }
  while (i < n) push(DiffType.Removed, a[start + i++]);
  while (j < m) push(DiffType.Added, b[start + j++]);
  push(DiffType.Equal, a.slice(aEnd).join(""));

  return segments;
}

// Markdown decoration is formatting, not content — stripped so a
// formatting-only rewrite scores near 100%.
const MARKDOWN_DECORATION = /[*_#`>]+/g;

/** Word-level similarity in [0, 1]: 2·LCS / (words A + words B) on
 *  markdown-normalized words. Null when the texts are too large to pair. */
export function textSimilarity(before: string, after: string): number | null {
  const a = before.replace(MARKDOWN_DECORATION, " ").match(/\S+/g) ?? [];
  const b = after.replace(MARKDOWN_DECORATION, " ").match(/\S+/g) ?? [];
  if (a.length === 0 && b.length === 0) return 1;
  const core = trimmedLcs(a, b);
  if (core === null) return null;
  const shared = core.start + (a.length - core.aEnd) + core.lcs[0];
  return (2 * shared) / (a.length + b.length);
}
